/**
 * SME Onboarding Redux Slice
 *
 * Manages state for the entire onboarding flow with Redux Persist
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApplicationSettings,
  AssessmentData,
  BillingContact,
  DemoSchedulingData,
  FlowType,
  InitialInfoData,
  OnboardingCompletionResult,
  OrganizationRegistrationData,
  PricingSummary,
  ProjectTemplate,
  SingleProjectData,
  SingleProjectRegistrationData,
} from '../types/common.types';

export interface SMEOnboardingState {
  // Authentication
  authToken: string | null;
  emailVerified: boolean;
  onboardingId: string | null;

  // User data
  initialInfo: InitialInfoData | null;

  // Flow data
  flowType: FlowType | null;
  demoScheduling: DemoSchedulingData | null;
  organizationRegistration: OrganizationRegistrationData | null;
  singleProjectRegistration: SingleProjectRegistrationData | null;
  assessmentData: AssessmentData | null;
  pricingSummary: PricingSummary | null;
  singleProjectData: SingleProjectData | null;
  billingContact: BillingContact | null;
  completionResult: OnboardingCompletionResult | null;

  // Application settings (cached from API)
  applicationSettings: ApplicationSettings | null;
  projectTemplates: ProjectTemplate[] | null;

  // Progress tracking
  currentStep: string;
  completedSteps: string[];
}

const initialState: SMEOnboardingState = {
  // Authentication
  authToken: null,
  emailVerified: false,
  onboardingId: null,

  // User data
  initialInfo: null,

  // Flow data
  flowType: null,
  demoScheduling: null,
  organizationRegistration: null,
  singleProjectRegistration: null,
  assessmentData: null,
  pricingSummary: null,
  singleProjectData: null,
  billingContact: null,
  completionResult: null,

  // Application settings
  applicationSettings: null,
  projectTemplates: null,

  // Progress
  currentStep: 'initial-info',
  completedSteps: [],
};

const smeOnboardingSlice = createSlice({
  name: 'smeOnboarding',
  initialState,
  reducers: {
    // Authentication actions
    setAuthToken: (state, action: PayloadAction<string>) => {
      state.authToken = action.payload;
    },
    setEmailVerified: (state, action: PayloadAction<boolean>) => {
      state.emailVerified = action.payload;
    },
    setOnboardingId: (state, action: PayloadAction<string>) => {
      state.onboardingId = action.payload;
    },

    // Initial info
    setInitialInfo: (state, action: PayloadAction<InitialInfoData>) => {
      state.initialInfo = action.payload;
      // Set flow type based on user type
      if (action.payload.userType === 'single-project') {
        state.flowType = 'single';
      } else {
        state.flowType = 'organization';
      }
    },
    setFlowType: (state, action: PayloadAction<FlowType>) => {
      state.flowType = action.payload;
    },

    // Demo scheduling
    setDemoScheduling: (state, action: PayloadAction<DemoSchedulingData>) => {
      state.demoScheduling = action.payload;
    },

    // Registration
    setOrganizationRegistration: (state, action: PayloadAction<OrganizationRegistrationData>) => {
      state.organizationRegistration = action.payload;
    },
    setSingleProjectRegistration: (state, action: PayloadAction<SingleProjectRegistrationData>) => {
      state.singleProjectRegistration = action.payload;
    },

    // Assessment
    setAssessmentData: (state, action: PayloadAction<AssessmentData>) => {
      state.assessmentData = action.payload;
    },

    // Pricing
    setPricingSummary: (state, action: PayloadAction<PricingSummary>) => {
      state.pricingSummary = action.payload;
    },
    setSingleProjectData: (state, action: PayloadAction<SingleProjectData>) => {
      state.singleProjectData = action.payload;
    },
    setBillingContact: (state, action: PayloadAction<BillingContact>) => {
      state.billingContact = action.payload;
    },
    setCompletionResult: (state, action: PayloadAction<OnboardingCompletionResult>) => {
      state.completionResult = action.payload;
    },

    // Application settings
    setApplicationSettings: (state, action: PayloadAction<ApplicationSettings>) => {
      state.applicationSettings = action.payload;
    },
    setProjectTemplates: (state, action: PayloadAction<ProjectTemplate[]>) => {
      state.projectTemplates = action.payload;
    },

    // Progress tracking
    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.currentStep = action.payload;
    },
    markStepComplete: (state, action: PayloadAction<string>) => {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
    },

    // Reset onboarding (after completion)
    resetOnboarding: () => initialState,
  },
});

export const {
  setAuthToken,
  setEmailVerified,
  setOnboardingId,
  setInitialInfo,
  setFlowType,
  setDemoScheduling,
  setOrganizationRegistration,
  setSingleProjectRegistration,
  setAssessmentData,
  setPricingSummary,
  setSingleProjectData,
  setBillingContact,
  setCompletionResult,
  setApplicationSettings,
  setProjectTemplates,
  setCurrentStep,
  markStepComplete,
  resetOnboarding,
} = smeOnboardingSlice.actions;

export default smeOnboardingSlice.reducer;
